// Typed JSON HTTP wrapper for the SPA → apps/server calls.
//
// All requests are credentialed — the signed `loom_session` cookie carries
// identity. On 401 we throw `UnauthorizedError`, and a 401 that means "your
// Loom sign-in is gone" also fires the app's ONE registered reaction (see
// `signalUnauthorized`). Route loaders bounce to /sign-in on a route MATCH,
// which a click inside an already-mounted surface is not — so before this the
// only reaction to a lapsed cookie was whatever the calling hook happened to
// render, and the history rail rendered nothing at all.
//
// Two 401 dialects reach this app and they want opposite reactions; they are
// told apart by response BODY, never by status alone.

import { z } from "zod";

export class HttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Raw response body, when the server sent one. Callers that want to
     *  render an operator-facing message parse this instead of scraping it
     *  back out of `message` (which is a debug string, not UI copy). */
    readonly body?: string,
  ) {
    super(message);
  }
}

const FailureBody = z.object({ error: z.string() });

/** What a failed call said: its HTTP status and closed code, when it sent one. */
export interface HttpFailure {
  readonly status: number | null;
  readonly code: string | null;
}

/** The status and the `{error: "<code>"}` a failed call answered, never the body. */
export const failureCodeOf = (err: unknown): HttpFailure => {
  if (!(err instanceof HttpError)) return { status: null, code: null };
  let code: string | null = null;
  try {
    const parsed = FailureBody.safeParse(JSON.parse(err.body ?? ""));
    code = parsed.success ? parsed.data.error : null;
  } catch {
    code = null;
  }
  return { status: err.status, code };
};

export class UnauthorizedError extends HttpError {
  constructor(message = "unauthorized") {
    super(message, 401);
  }
}

let unauthorizedHandler: (() => void) | null = null;
let signalled = false;

/**
 * The app's ONE reaction to "your Loom sign-in is gone". Registered once in
 * `main.tsx`. Every site that mints `UnauthorizedError` must route through
 * `signalUnauthorized` — there are two outside this file (`useDecompose`,
 * `useCreateIssue`), and they call it directly.
 */
export const setUnauthorizedHandler = (fn: () => void): void => {
  unauthorizedHandler = fn;
};

/**
 * Two different 401s reach this app and they want opposite reactions:
 *   {"error":"no_session"}      the loom_session cookie is gone → sign in again.
 *   {"error":"reauth_required"} the cookie is fine, the GitLab token is dead.
 *                               Recovered in place; yanking the SPA to /sign-in
 *                               would throw away whatever the user was mid-way
 *                               through. Never signalled from here.
 * `/auth/me`'s 401 IS the route loader's own signal (`fetchMe` maps it to null
 * and `requireApproved` redirects), so reacting to it would double-navigate
 * every ordinary unauthenticated landing.
 *
 * The latch is self-releasing — see the success path in `jsonOrThrow`: a
 * request that succeeds proves the cookie is good again. Without that, one 401
 * arriving while the user already sits on /sign-in would disarm the reaction
 * for the whole document lifetime.
 */
export const signalUnauthorized = (path: string, body: string): void => {
  if (path === "/auth/me") return;
  if (body.includes("reauth_required")) return;
  if (signalled) return;
  signalled = true;
  try {
    unauthorizedHandler?.();
  } catch {
    // never block the throw the caller is waiting on
  }
};

// `path` is the CALLER's own literal, not `res.url`: a hand-built
// `new Response(...)` — the shape every fetch double in this repo uses — has
// `url === ""`, so a URL-based exemption would silently never apply under test.
const jsonOrThrow = async <T>(
  res: Response,
  schema: z.ZodType<T>,
  path: string,
): Promise<T> => {
  if (res.status === 401) {
    const body = await res.text().catch(() => "");
    signalUnauthorized(path, body);
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    let detail = "";
    try {
      detail = await res.text();
    } catch {
      // ignore
    }
    throw new HttpError(
      `${res.status} ${res.statusText}${detail ? `: ${detail}` : ""}`,
      res.status,
      detail || undefined,
    );
  }
  signalled = false;
  const body: unknown = await res.json();
  return schema.parse(body);
};

export const httpGet = async <T>(
  path: string,
  schema: z.ZodType<T>,
  init?: RequestInit,
): Promise<T> => {
  const res = await fetch(path, {
    method: "GET",
    credentials: "same-origin",
    headers: { accept: "application/json" },
    ...init,
  });
  return jsonOrThrow(res, schema, path);
};

export const httpPost = async <T>(
  path: string,
  body: unknown,
  schema: z.ZodType<T>,
  init?: RequestInit,
): Promise<T> => {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify(body),
    ...init,
  });
  return jsonOrThrow(res, schema, path);
};

export const httpPatch = async <T>(
  path: string,
  body: unknown,
  schema: z.ZodType<T>,
  init?: RequestInit,
): Promise<T> => {
  const res = await fetch(path, {
    method: "PATCH",
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify(body),
    ...init,
  });
  return jsonOrThrow(res, schema, path);
};

export const httpPut = async <T>(
  path: string,
  body: unknown,
  schema: z.ZodType<T>,
  init?: RequestInit,
): Promise<T> => {
  const res = await fetch(path, {
    method: "PUT",
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify(body),
    ...init,
  });
  return jsonOrThrow(res, schema, path);
};

export const httpDelete = async (
  path: string,
  init?: RequestInit,
): Promise<void> => {
  const res = await fetch(path, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { accept: "application/json" },
    ...init,
  });
  if (res.status === 401) {
    signalUnauthorized(path, await res.text().catch(() => ""));
    throw new UnauthorizedError();
  }
  if (!res.ok && res.status !== 204) {
    throw new HttpError(`${res.status} ${res.statusText}`, res.status);
  }
};

/**
 * A POST whose success is 204 with no body. Same 401 handling as the JSON
 * helpers, so a lapsed sign-in reaches `signalUnauthorized`.
 */
export const httpPostNoContent = async (
  path: string,
  body: unknown,
  init?: RequestInit,
): Promise<void> => {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify(body),
    ...init,
  });
  if (res.status === 401) {
    signalUnauthorized(path, await res.text().catch(() => ""));
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new HttpError(
      `${res.status} ${res.statusText}${detail ? `: ${detail}` : ""}`,
      res.status,
      detail || undefined,
    );
  }
  signalled = false;
};
